import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Trophy, Search, X } from 'lucide-react';
import { Seo, breadcrumbSchema, webPageSchema } from '../components/Seo';
import PageHeader from '../components/PageHeader';
import Reveal from '../components/Reveal';
import { EVENT_DATE_LABEL, EVENT_VENUE_LABEL } from '../data/event';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER = {
  gold: { label: 'Gold', bg: 'linear-gradient(135deg,#D9B26A 0%,#B8884A 55%,#8B6532 100%)', fg: '#FFFDF7', line: '#B8884A' },
  silver: { label: 'Silver', bg: 'linear-gradient(135deg,#D8D8D8 0%,#A9A9A9 55%,#7D7D7D 100%)', fg: '#FFFFFF', line: '#A9A9A9' },
  bronze: { label: 'Bronze', bg: 'linear-gradient(135deg,#C98B5E 0%,#9A6B3F 55%,#6F4A2A 100%)', fg: '#FFF8F2', line: '#9A6B3F' },
};
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const Placeholder = () => (
  <Reveal>
    <div className="max-w-3xl border border-[#C9A56C]/40 bg-[#FAF3E7] p-10 md:p-14 relative overflow-hidden" data-testid="winners-placeholder">
      <Trophy size={28} className="text-[#B8884A]" />
      <div className="mt-6 text-[10px] tracking-[0.35em] uppercase text-[#7A4E14] font-semibold">Winners announced on 8th October</div>
      <h2 className="font-serif text-[#2D1810] text-2xl md:text-3xl mt-4 leading-tight">The room is being curated.</h2>
      <p className="text-[#634F3A] text-sm md:text-[15px] leading-relaxed mt-5">
        Every AdAura winner, across all pillars and categories, will be revealed live on awards night and published here immediately after the ceremony. Until then, the jury deliberates in confidence.
      </p>
    </div>
  </Reveal>
);

const Medal = ({ tier }) => {
  const t = TIER[tier] || TIER.gold;
  return (
    <span data-testid={`medal-${tier}`} className="inline-flex items-center justify-center min-w-[84px] px-3 py-1.5 text-[9.5px] tracking-[0.32em] uppercase font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.35)]" style={{ background: t.bg, color: t.fg }}>
      {t.label}
    </span>
  );
};

const WinnerRow = ({ w }) => {
  const meta = [w.organisation && { k: 'Brand', v: w.organisation }, w.agency && { k: 'Agency', v: w.agency }].filter(Boolean);
  return (
    <li className="grid grid-cols-1 md:grid-cols-[110px_minmax(0,1.3fr)_minmax(0,1fr)] gap-3 md:gap-8 items-start py-5 md:py-6 border-t border-[#3A2418]/10 first:border-t-0" data-testid={`winner-row-${w.id}`}>
      <div className="pt-0.5"><Medal tier={w.tier} /></div>
      <div className="min-w-0">
        <div className="font-serif text-[#2D1810] text-lg md:text-[1.25rem] leading-snug" data-testid="winner-awarded-to">{w.awarded_to || w.organisation}</div>
        {w.entry_title && <div className="italic text-[#634F3A] text-[14px] md:text-[15px] leading-relaxed mt-1" data-testid="winner-entry-title">{w.entry_title}</div>}
      </div>
      <div className="min-w-0 text-[13px] text-[#634F3A] space-y-1 md:pt-1">
        {meta.map((m) => (
          <div key={m.k} className="flex gap-3"><span className="text-[9.5px] tracking-[0.3em] uppercase text-[#9F6A2C] pt-[3px] w-14 shrink-0">{m.k}</span><span className="leading-relaxed">{m.v}</span></div>
        ))}
        {w.citation && <p className="italic text-[#634F3A]/85 leading-relaxed pt-1">{w.citation}</p>}
      </div>
    </li>
  );
};

const groupWinners = (winners) => {
  const pillars = [];
  const byPillar = new Map();
  winners.forEach((w) => {
    if (!byPillar.has(w.pillar)) { byPillar.set(w.pillar, new Map()); pillars.push(w.pillar); }
    const cats = byPillar.get(w.pillar);
    if (!cats.has(w.category)) cats.set(w.category, []);
    cats.get(w.category).push(w);
  });
  return pillars.map((p, i) => ({ pillar: p, number: String(i + 1).padStart(2, '0'), categories: [...byPillar.get(p).entries()] }));
};

const PillarNav = ({ groups, query, setQuery, total, shown }) => (
  <div className="sticky top-[64px] md:top-[72px] z-20 -mx-6 px-6 md:-mx-12 md:px-12 py-3 bg-[#F7F0E4]/90 backdrop-blur-md border-b border-[#3A2418]/10" data-testid="winners-toolbar">
    <div className="content-container flex flex-wrap items-center gap-3 md:gap-4">
      <nav className="flex-1 min-w-0 flex gap-2 overflow-x-auto no-scrollbar py-1" aria-label="Jump to pillar">
        {groups.map((g) => (
          <a key={g.pillar} href={`#${slug(g.pillar)}`} data-testid={`winners-nav-${slug(g.pillar)}`} className="shrink-0 border border-[#3A2418]/15 hover:border-[#B8884A] hover:text-[#7A4E14] text-[#3A2418] text-[10px] tracking-[0.22em] uppercase px-3.5 py-2 transition-colors whitespace-nowrap">
            <span className="text-[#B8884A] mr-1.5">{g.number}</span>{g.pillar}
          </a>
        ))}
      </nav>
      <label className="relative flex items-center shrink-0 w-full sm:w-64">
        <Search size={14} className="absolute left-3 text-[#9F6A2C]" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search brand, agency or campaign" data-testid="winners-search" className="w-full bg-white border border-[#3A2418]/15 focus:border-[#B8884A] outline-none pl-9 pr-9 py-2 text-[13px] text-[#2D1810] placeholder:text-[#634F3A]/55" />
        {query && <button type="button" onClick={() => setQuery('')} aria-label="Clear search" data-testid="winners-search-clear" className="absolute right-2 text-[#634F3A] hover:text-[#2D1810]"><X size={14} /></button>}
      </label>
      <span className="text-[10px] tracking-[0.25em] uppercase text-[#634F3A]/70 shrink-0" data-testid="winners-count">{shown === total ? `${total} honours` : `${shown} of ${total}`}</span>
    </div>
  </div>
);

const HonourRoll = ({ winners }) => {
  const [query, setQuery] = useState('');
  const allGroups = useMemo(() => groupWinners(winners), [winners]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return winners;
    return winners.filter((w) => [w.awarded_to, w.organisation, w.agency, w.entry_title, w.category, w.pillar].some((v) => v && v.toLowerCase().includes(q)));
  }, [winners, query]);
  const groups = useMemo(() => groupWinners(filtered), [filtered]);
  const numberOf = (p) => allGroups.find((g) => g.pillar === p)?.number || '';

  return (
    <div data-testid="winners-honour-roll">
      <PillarNav groups={allGroups} query={query} setQuery={setQuery} total={winners.length} shown={filtered.length} />

      {groups.length === 0 && (
        <p className="py-16 text-[#634F3A] text-sm" data-testid="winners-no-results">No honours match “{query}”.</p>
      )}

      <div className="space-y-24 md:space-y-32 pt-14 md:pt-20">
        {groups.map((g) => (
          <section key={g.pillar} id={slug(g.pillar)} className="scroll-mt-40" data-testid={`winners-pillar-${slug(g.pillar)}`}>
            <Reveal>
              <div className="flex items-end gap-5 md:gap-7 mb-10 md:mb-14">
                <span className="font-serif text-[#B8884A] text-4xl md:text-6xl leading-none">{numberOf(g.pillar)}</span>
                <div className="pb-1">
                  <div className="text-[10px] tracking-[0.35em] uppercase text-[#9F6A2C] mb-2">Pillar</div>
                  <h2 className="font-serif text-[#2D1810] text-2xl md:text-4xl leading-none tracking-tight">{g.pillar}</h2>
                </div>
                <span aria-hidden className="hidden md:block flex-1 h-px bg-gradient-to-r from-[#B8884A]/50 to-transparent mb-3" />
              </div>
            </Reveal>

            <div className="space-y-10 md:space-y-12">
              {g.categories.map(([cat, items]) => (
                <Reveal key={cat}>
                  <div className="bg-[#FAF3E7] border border-[#C9A56C]/35 px-6 md:px-9 py-6 md:py-7 relative" data-testid={`winners-category-${slug(cat)}`}>
                    <span aria-hidden className="absolute top-0 left-0 h-[2px] w-16 bg-[#B8884A]" />
                    <h3 className="text-[11px] md:text-[12px] tracking-[0.32em] uppercase text-[#7A4E14] font-semibold mb-2">{cat}</h3>
                    <ul>{items.map((w) => <WinnerRow key={w.id} w={w} />)}</ul>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

const Winners = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/winners`).then((r) => setData(r.data)).catch(() => setData({ announced: false, winners: [] }));
  }, []);
  const live = !!(data?.announced && data.winners?.length);

  return (
    <div data-testid="page-winners">
      <Seo
        title="Winners · AdAura Awards 2026"
        description="AdAura Awards 2026 winners — Gold, Silver and Bronze across every category. Announced on 8th October 2026 at Aerocity, New Delhi."
        path="/winners"
        jsonLd={[
          webPageSchema({ path: '/winners', name: 'Winners', description: 'AdAura Awards 2026 winners — announced on 8th October 2026.' }),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Winners', path: '/winners' }]),
        ]}
      />
      <PageHeader
        eyebrow="Honour Roll"
        title="Winners"
        subtitle={live ? undefined : `Winners announced on ${EVENT_DATE_LABEL} at ${EVENT_VENUE_LABEL}.`}
      />
      <section className="pb-24 md:pb-32">
        <div className="content-container">
          {data === null ? (
            <div className="py-10 text-[#634F3A] tracking-[0.3em] uppercase text-xs" data-testid="winners-loading">Loading…</div>
          ) : live ? (
            <HonourRoll winners={data.winners} />
          ) : (
            <Placeholder />
          )}
        </div>
      </section>
    </div>
  );
};

export default Winners;
